import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import {
    Rate,
    SaveRateRule,
    RateRuleOperator,
    RateItemType,
    EditRateItemAttribute,
    RateRuleAttribute
} from '@spike/boarding-rule-model';
import { OccupancyRule, OccupancyOperator, BoardingServiceItem } from './types';

const operatorToModel: Record<OccupancyOperator, RateRuleOperator> = {
    greater_than: 'gt',
    greater_than_or_equal: 'gte',
    less_than: 'lt',
    less_than_or_equal: 'lte'
};

const operatorFromModel: Record<RateRuleOperator, OccupancyOperator> = {
    gt: 'greater_than',
    gte: 'greater_than_or_equal',
    lt: 'less_than',
    lte: 'less_than_or_equal'
};

const defaultOccupancyRule = (): OccupancyRule => ({
    id: uuid(),
    operator: 'greater_than',
    percentage: 75,
    action: 'increase',
    amount: 0,
    currency: 'USD'
});

/**
 * Class-based form state for RateRuleForm that encapsulates:
 * - Form field values
 * - Edit mode tracking (stores original for diff)
 * - Serialization to SaveRate for API submission
 */
export class RateRuleFormState {
    name = '';
    active = true;
    datesEnabled = false;
    startDate: Date | null = null;
    endDate: Date | null = null;
    minNights = 1;
    services: BoardingServiceItem[] = [];
    occupancyRules: OccupancyRule[] = [defaultOccupancyRule()];

    private _originalRate: Rate | null = null;
    private _marketplaceId: number | null = null;

    /**
     * Create empty form state for "Add" mode
     */
    static createEmpty(): RateRuleFormState {
        return new RateRuleFormState();
    }

    /**
     * Create form state from existing Rate for "Edit" mode
     */
    static fromRateRule(rate: Rate, availableServices: { id?: number; name: string }[] = []): RateRuleFormState {
        const state = new RateRuleFormState();

        state._originalRate = rate;
        state._marketplaceId = rate.marketplaceId;

        state.name = rate.name || '';
        state.active = rate.active;
        state.minNights = rate.minNights;
        state.datesEnabled = !!(rate.startDate?.isValid() || rate.endDate?.isValid());
        state.startDate = rate.startDate?.isValid() ? rate.startDate.toDate() : null;
        state.endDate = rate.endDate?.isValid() ? rate.endDate.toDate() : null;

        state.services = (rate.rateItems ?? []).map(ri => ({
            id: uuid(),
            serviceId: ri.itemId,
            serviceName: availableServices.find(s => s.id === ri.itemId)?.name ?? '',
            standardRate: ri.amount
        }));

        const rateRulesWithRule = (rate.rateRules ?? []).filter(rr => rr.rule);
        state.occupancyRules =
            rateRulesWithRule.length > 0
                ? rateRulesWithRule.map(rr => ({
                      id: uuid(),
                      rateRuleId: rr.id,
                      operator: operatorFromModel[rr.rule!.condition.operator],
                      percentage: rr.rule!.condition.threshold,
                      action: rr.rule!.action.type,
                      amount: rr.rule!.action.value,
                      currency:
                          rr.rule!.action.valueType === 'percentage' ? 'PERCENT' : rr.rule!.action.currency ?? undefined
                  }))
                : [defaultOccupancyRule()];

        return state;
    }

    /**
     * Check if this is edit mode
     */
    get isEditMode(): boolean {
        return this._originalRate !== null;
    }

    /**
     * Get the original rate ID (for edit mode)
     */
    get originalId(): number | undefined {
        return this._originalRate?.id;
    }

    /**
     * Convert to SaveRate for API submission.
     * In edit mode uses diff logic: KEEP/DELETE/ADD for both items and rules.
     */
    toSaveRateRule(marketplaceId: number): SaveRateRule {
        const effectiveMarketplaceId = this._marketplaceId || marketplaceId;

        return {
            id: this._originalRate?.id,
            name: this.name,
            marketplaceId: effectiveMarketplaceId,
            startDate: this.datesEnabled && this.startDate ? moment(this.startDate) : undefined,
            endDate: this.datesEnabled && this.endDate ? moment(this.endDate) : undefined,
            minNights: this.minNights,
            active: this.active,
            customerTagList: this._originalRate?.customerTagList ?? [],
            rateItemsAttributes: this._buildRateItemsAttributes(effectiveMarketplaceId),
            rateRulesAttributes: this._buildRateRulesAttributes(effectiveMarketplaceId)
        };
    }

    /**
     * Build rate items with KEEP/DELETE/ADD diff logic for edit mode.
     */
    private _buildRateItemsAttributes(marketplaceId: number): EditRateItemAttribute[] {
        if (!this._originalRate) {
            // Create mode: no IDs
            return this.services.map(service => ({
                marketplaceId,
                itemId: service.serviceId,
                itemType: RateItemType.SERVICE,
                quantity: 1,
                amount: service.standardRate
            }));
        }

        const result: EditRateItemAttribute[] = [];
        const originalItems = this._originalRate.rateItems ?? [];
        const originalItemMap = new Map(originalItems.map(item => [item.itemId, item]));

        // KEEP or DELETE original items
        originalItems.forEach(original => {
            const current = this.services.find(s => s.serviceId === original.itemId);
            if (current) {
                // KEEP: only send the rate_item id + updatable fields, never item_id
                result.push({
                    id: original.id,
                    quantity: 1,
                    amount: current.standardRate
                });
            } else {
                // DELETE
                result.push({ id: original.id, _destroy: true });
            }
        });

        // ADD new items that didn't exist originally — item_id required here
        this.services.forEach(service => {
            if (!originalItemMap.has(service.serviceId)) {
                result.push({
                    marketplaceId,
                    itemId: service.serviceId,
                    itemType: RateItemType.SERVICE,
                    quantity: 1,
                    amount: service.standardRate
                });
            }
        });

        return result;
    }

    /**
     * Build rate rules attributes with KEEP/DELETE/ADD diff logic for edit mode.
     */
    private _buildRateRulesAttributes(marketplaceId: number): RateRuleAttribute[] {
        const buildRule = (rule: OccupancyRule): RateRuleAttribute['rule'] => ({
            condition: {
                metric: 'occupancy',
                operator: operatorToModel[rule.operator],
                threshold: rule.percentage
            },
            action: {
                type: rule.action,
                value: rule.amount,
                valueType: rule.currency === 'PERCENT' ? 'percentage' : 'fixed',
                currency: rule.currency === 'PERCENT' ? undefined : rule.currency
            }
        });

        if (!this._originalRate) {
            // Create mode: no IDs
            return this.occupancyRules.map(rule => ({
                name: this.name,
                marketplaceId,
                amount: 1,
                active: true,
                rule: buildRule(rule)
            }));
        }

        const result: RateRuleAttribute[] = [];
        const originalRules = this._originalRate.rateRules ?? [];
        const originalRuleIds = new Set(originalRules.map(r => r.id));

        // KEEP or DELETE original rules
        originalRules.forEach(original => {
            const current = this.occupancyRules.find(r => r.rateRuleId === original.id);
            if (current) {
                result.push({
                    id: original.id,
                    name: this.name,
                    marketplaceId,
                    amount: 1,
                    active: true,
                    rule: buildRule(current)
                });
            } else {
                result.push({ id: original.id, _destroy: true });
            }
        });

        // ADD new rules that didn't exist originally
        this.occupancyRules.forEach(rule => {
            if (!rule.rateRuleId || !originalRuleIds.has(rule.rateRuleId)) {
                result.push({
                    name: this.name,
                    marketplaceId,
                    amount: 1,
                    active: true,
                    rule: buildRule(rule)
                });
            }
        });

        return result;
    }

    /**
     * Clone for immutable updates (React state pattern)
     */
    clone(): RateRuleFormState {
        const cloned = new RateRuleFormState();
        cloned.name = this.name;
        cloned.active = this.active;
        cloned.datesEnabled = this.datesEnabled;
        cloned.startDate = this.startDate;
        cloned.endDate = this.endDate;
        cloned.minNights = this.minNights;
        cloned.services = this.services.map(s => ({ ...s }));
        cloned.occupancyRules = this.occupancyRules.map(r => ({ ...r }));
        cloned._originalRate = this._originalRate;
        cloned._marketplaceId = this._marketplaceId;
        return cloned;
    }

    /**
     * Create updated state with new values (immutable pattern for React)
     * Usage: setFormState(prev => prev.with({ name: newName }))
     */
    with(
        updates: Partial<
            Pick<
                RateRuleFormState,
                'name' | 'active' | 'datesEnabled' | 'startDate' | 'endDate' | 'services' | 'occupancyRules'
            >
        >
    ): RateRuleFormState {
        const cloned = this.clone();
        Object.assign(cloned, updates);
        return cloned;
    }
}
